import { Pencil } from 'lucide-react';
import { IconButton } from '../../ui/index.js';
import { useLayoutEffect, useRef } from 'react';

export function InlineShootNotes({
  id = 'shoot-notes',
  className = '',
  value,
  editing,
  busy,
  disabled,
  dirty,
  onEdit,
  onChange,
  onSave,
  onCancel,
}: {
  id?: string;
  className?: string;
  value: string;
  editing: boolean;
  busy: boolean;
  disabled: boolean;
  dirty: boolean;
  onEdit(): void;
  onChange(value: string): void;
  onSave(): Promise<void>;
  onCancel(): void;
}) {
  const field = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const textarea = field.current;
    if (!editing || !textarea) return;
    const fit = () => {
      const style = getComputedStyle(textarea);
      const lineHeight = parseFloat(style.lineHeight);
      const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
      const border = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
      const maxHeight = lineHeight * 10 + padding + border;
      textarea.style.height = 'auto';
      const height = Math.min(textarea.scrollHeight + border, maxHeight);
      textarea.style.height = `${height}px`;
      textarea.style.overflowY = textarea.scrollHeight + border > maxHeight ? 'auto' : 'hidden';
    };
    fit();
    let width = textarea.getBoundingClientRect().width;
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            const nextWidth = textarea.getBoundingClientRect().width;
            if (nextWidth !== width) {
              width = nextWidth;
              fit();
            }
          });
    observer?.observe(textarea);
    window.addEventListener('resize', fit);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [value, editing]);
  return (
    <section
      className={`shoot-notes crm-detail-section ${className}`}
      aria-labelledby={`${id}-title`}
    >
      <div>
        <h2 id={`${id}-title`} className="crm-section-title">
          Notes
        </h2>
        {!editing ? (
          <IconButton
            variant="quiet"
            icon={Pencil}
            label="Edit notes"
            disabled={disabled}
            onClick={onEdit}
          />
        ) : null}
      </div>
      {editing ? (
        <>
          <textarea
            ref={field}
            autoFocus
            aria-label="Notes"
            className="shoot-notes__input"
            rows={1}
            value={value}
            disabled={busy || disabled}
            onChange={(event) => onChange(event.target.value)}
          />
          <div className="shoot-notes__actions">
            <button
              className="shoot-button shoot-button--outline"
              disabled={busy}
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              className="shoot-button shoot-button--primary"
              aria-busy={busy}
              disabled={busy || disabled || !dirty}
              onClick={() => void onSave()}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </>
      ) : (
        <p>{value || 'No notes yet.'}</p>
      )}
    </section>
  );
}
